/**
 * The Org screen's Teams-tab import/export set, offered again on the Operations
 * panel's root competition. Every file here is ORGANIZATION-wide — the API has
 * no competition-scoped team or roster transfer — so the endpoints and file
 * names are the Org screen's own, unchanged.
 */
export type OrgTransferKind = 'teams' | 'playerRosters' | 'officialRosters'

export type OrgTransferDirection = 'import' | 'export'

/** Menu order, matching the Org screen's dropdowns. */
export const ORG_TRANSFER_KINDS: readonly OrgTransferKind[] = [
  'teams',
  'playerRosters',
  'officialRosters',
]

/**
 * Imports that registration owns. While a competition's registration is on,
 * teams and their players arrive through the Registration tab — importing them
 * by hand behind it creates teams and players the registration flow does not
 * know about. Team officials are not registered, so their import stays open.
 * Exports only read, and are never blocked.
 */
const REGISTRATION_OWNED_IMPORTS: ReadonlySet<OrgTransferKind> = new Set([
  'teams',
  'playerRosters',
])

/** Whether an import is unavailable because the competition's registration is on. */
export const isOrgImportBlockedByRegistration = (
  kind: OrgTransferKind,
  isRegistrationOn: boolean
): boolean => isRegistrationOn && REGISTRATION_OWNED_IMPORTS.has(kind)

const EXPORT_PATH: Record<OrgTransferKind, string> = {
  teams: 'teams/export/csv',
  playerRosters: 'teams/exportmulti/csv',
  officialRosters: 'teams/team-officials/exportmulti/csv',
}

export const orgExportUrl = (orgId: string, kind: OrgTransferKind): string =>
  `/organizations/${orgId}/${EXPORT_PATH[kind]}`

const FILE_SUFFIX: Record<OrgTransferKind, string> = {
  teams: 'teams',
  playerRosters: 'player_rosters',
  officialRosters: 'team_official_rosters',
}

/**
 * "Planet Ice" + teams -> "Planet_Ice_teams" (the download appends `.csv`) —
 * the Org screen's naming, so the same export saves under the same name from
 * either place.
 */
export const orgExportFileName = (
  orgName: string,
  kind: OrgTransferKind
): string =>
  `${(orgName.trim() || 'organization').replace(/\s+/g, '_')}_${
    FILE_SUFFIX[kind]
  }`

type Translate = (key: string, options?: unknown) => string

/**
 * Only the Teams entries have i18n keys. The roster entries mirror the Org
 * screen's own untranslated labels word for word, so the two menus read alike.
 */
const LABEL: Record<
  OrgTransferDirection,
  Record<OrgTransferKind, (translate: Translate) => string>
> = {
  import: {
    teams: (translate) => translate('common:importTeams'),
    playerRosters: () => 'Import Player Rosters',
    officialRosters: () => 'Import Team Official Rosters',
  },
  export: {
    teams: (translate) => translate('common:exportTeams'),
    playerRosters: () => 'Export Player Rosters',
    officialRosters: () => 'Export Team Official Rosters',
  },
}

/** Menu label for one transfer. The translator is injected, as elsewhere here. */
export const orgTransferLabel = (
  direction: OrgTransferDirection,
  kind: OrgTransferKind,
  translate: Translate
): string => LABEL[direction][kind](translate)

/** The icon trigger's own name. `common:import` exists; there is no export key. */
export const orgTransferTriggerLabel = (
  direction: OrgTransferDirection,
  translate: Translate
): string => (direction === 'import' ? translate('common:import') : 'Export')

/** Narrows a menu key back to a kind — null for anything the menus never emit. */
export const parseOrgTransferKind = (key: string): OrgTransferKind | null =>
  ORG_TRANSFER_KINDS.find((kind) => kind === key) ?? null
